import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [previousArrow]
 * @property {HTMLButtonElement} [nextArrow]
 * @property {HTMLElement} [panel]
 * @property {HTMLElement} [status]
 * @property {HTMLButtonElement} [pauseButton]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [dots]
 */

/**
 * Story slideshow with chevron arrows and dot pagination.
 *
 * @extends {Component<Refs>}
 */
class StorySlidesComponent extends Component {

  /** @type {number} */
  #index = 0;

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {number | undefined} */
  #timer;

  /** @type {boolean} */
  #autoplayPaused = false;

  /** @type {boolean} */
  #hoverPaused = false;

  /** @type {number | null} */
  #pointerStartX = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('focusin', this.#onPointerEnter, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    const initial = Number(this.dataset.activeIndex ?? '0');
    this.#index = Number.isNaN(initial) ? 0 : initial;
    this.#sync(false);
    this.#startAutoplay();
  }

  updatedCallback() {
    super.updatedCallback();
    const count = this.#slideCount();
    if (count === 0) return;
    if (this.#index >= count) this.#index = count - 1;
    this.#sync(false);
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#stopAutoplay();
    super.disconnectedCallback();
  }

  /**
   * Show the slide at the given index.
   * @param {number} index
   * @param {Event} [event]
   */
  select(index, event) {
    if (event instanceof Event && event.type === 'click') event.preventDefault();
    const count = this.#slideCount();
    if (count === 0) return;

    const next = ((Number(index) % count) + count) % count;
    const changed = next !== this.#index;
    let direction = next < this.#index ? 'previous' : 'next';
    if (this.#index === count - 1 && next === 0) direction = 'next';
    if (this.#index === 0 && next === count - 1) direction = 'previous';
    this.#index = next;
    this.#sync(changed, direction);
    if (changed) this.#startAutoplay();
  }

  /**
   * Show the previous slide.
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    if (this.#index <= 0) return;
    this.select(this.#index - 1);
  }

  /**
   * Show the next slide.
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    if (this.#index >= this.#slideCount() - 1) return;
    this.select(this.#index + 1);
  }

  /**
   * Pause or resume automatic playback.
   * @param {Event} [event]
   */
  toggleAutoplay(event) {
    event?.preventDefault();
    this.#autoplayPaused = !this.#autoplayPaused;
    if (this.#autoplayPaused) this.#stopAutoplay();
    else this.#startAutoplay();
    this.#updatePauseButton();
  }

  #slideCount() {
    return this.refs.slides?.length ?? 0;
  }

  /**
   * @param {boolean} announce
   * @param {'next' | 'previous'} [direction]
   */
  #sync(announce, direction = 'next') {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    for (const slide of this.refs.slides ?? []) {
      slide.classList.remove('is-entering', 'is-entering-previous');
    }

    if (announce && !reduceMotion) {
      void this.offsetWidth;
    }

    for (const slide of this.refs.slides ?? []) {
      const selected = Number(slide.dataset.index) === this.#index;
      slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
      if (selected || window.Shopify?.designMode) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');

      if (selected && announce && !reduceMotion) {
        slide.classList.add(direction === 'previous' ? 'is-entering-previous' : 'is-entering');
      }
    }

    this.#syncArrows();

    for (const dot of this.refs.dots ?? []) {
      const selected = Number(dot.dataset.index) === this.#index;
      if (selected) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }

    if (!announce || !this.refs.status) return;
    const active = (this.refs.slides ?? []).find((slide) => Number(slide.dataset.index) === this.#index);
    this.refs.status.textContent = active?.dataset.announcement ?? '';
  }

  #syncArrows() {
    const count = this.#slideCount();
    const previous = this.refs.previousArrow;
    const next = this.refs.nextArrow;

    if (previous) previous.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= count - 1;
  }

  #startAutoplay() {
    this.#stopAutoplay();
    if (this.dataset.autoplay !== 'true') return;
    if (this.#autoplayPaused || this.#hoverPaused) return;
    if (window.Shopify?.designMode) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.#slideCount() < 2) return;

    const seconds = Number(this.dataset.autoplaySpeed || '6');
    const delay = Math.max(Number.isNaN(seconds) ? 6 : seconds, 3) * 1000;
    this.#timer = window.setInterval(() => {
      this.select((this.#index + 1) % this.#slideCount());
    }, delay);
  }

  #stopAutoplay() {
    if (this.#timer === undefined) return;
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #updatePauseButton() {
    const button = this.refs.pauseButton;
    if (!button) return;
    const label = button.querySelector('.story-slides__pause-label');
    const nextLabel = this.#autoplayPaused ? button.dataset.playLabel : button.dataset.pauseLabel;
    if (label && nextLabel) label.textContent = nextLabel;
    button.setAttribute('aria-pressed', this.#autoplayPaused ? 'true' : 'false');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.closest('.story-slides__arrow, .story-slides__dot')) return;

    const count = this.#slideCount();
    if (count < 2) return;

    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (this.#index + 1) % count;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (this.#index - 1 + count) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    if (next === null) return;

    event.preventDefault();
    this.select(next);
    const dot = (this.refs.dots ?? []).find((item) => Number(item.dataset.index) === next);
    dot?.focus();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.story-slides__panel')) return;
    if (event.target.closest('a, button')) return;
    this.#pointerStartX = event.clientX;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (this.#pointerStartX === null) return;
    const delta = event.clientX - this.#pointerStartX;
    this.#pointerStartX = null;
    if (Math.abs(delta) < 48) return;

    const count = this.#slideCount();
    if (count < 2) return;
    if (delta < 0) this.select((this.#index + 1) % count);
    else this.select((this.#index - 1 + count) % count);
  };

  #onPointerEnter = () => {
    this.#hoverPaused = true;
    this.#stopAutoplay();
  };

  #onPointerLeave = () => {
    this.#hoverPaused = false;
    if (this.contains(document.activeElement)) return;
    this.#startAutoplay();
  };

  /**
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#hoverPaused = false;
    this.#startAutoplay();
  };

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const slide = event.target.closest('[data-story-slide]');
    if (!(slide instanceof HTMLElement) || !this.contains(slide)) return;

    this.#autoplayPaused = true;
    this.#stopAutoplay();
    this.#updatePauseButton();
    this.select(Number(slide.dataset.index));
  };
}

if (!customElements.get('story-slides-component')) {
  customElements.define('story-slides-component', StorySlidesComponent);
}
